import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppState } from '@app/store/state/app.state';
import {
  cancelSignagePairingRequestOnServer,
  createSignagePairingRequest,
  getSignageAway,
  getSignageFloorPlan,
  getSignageFloors,
  getSignageOccupancy,
  pollSignagePairingRequest,
  SignageAway,
  SignageFloor,
  SignageFloorPlan,
  SignageOccupancy,
  SignagePairingRequestCreatedResponse,
  SignagePairingRequestPollResponse,
  unpairSignage,
} from './signage-service';
import {
  clearSignageSession,
  loadSelectedFloorId,
  loadSignageSession,
  saveSelectedFloorId,
  saveSignageSession,
} from '../storage/signageStorage';
import { SignageDisabledError, SignageUnauthorizedError } from './signage-agent';

type SignagePairingUiStatus = 'idle' | 'pending' | 'approved' | 'rejected' | 'expired' | 'error';

export interface SignageState {
  paired: boolean;
  companyId: string | null;
  companyName: string;
  officeId: number | null;
  officeLabel: string;
  pairing: boolean;
  pairingStatus: SignagePairingUiStatus;
  pendingRequestId: string | null;
  pendingExpiresAt: string | null;
  pollIntervalMs: number;
  pollFailureCount: number;
  pairingError: string | null;
  floors: SignageFloor[];
  floorsLoaded: boolean;
  selectedFloorId: number | null;
  floorPlan: SignageFloorPlan | null;
  floorPlanLoading: boolean;
  occupancy: SignageOccupancy | null;
  // Office-wide, not per floor; only fetched while an away board is showing
  away: SignageAway | null;
  // Signage keeps showing the last good data through network blips; this
  // timestamp drives the "last updated" staleness indicator.
  lastUpdatedAt: number | null;
  disabled: boolean;
  lastError: string | null;
}

const DEFAULT_POLL_INTERVAL_MS = 2000;
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

const initialFromStorage = (): SignageState => {
  const stored = loadSignageSession();
  const base: SignageState = {
    paired: false,
    companyId: null,
    companyName: '',
    officeId: null,
    officeLabel: '',
    pairing: false,
    pairingStatus: 'idle',
    pendingRequestId: null,
    pendingExpiresAt: null,
    pollIntervalMs: DEFAULT_POLL_INTERVAL_MS,
    pollFailureCount: 0,
    pairingError: null,
    floors: [],
    floorsLoaded: false,
    selectedFloorId: loadSelectedFloorId(),
    floorPlan: null,
    floorPlanLoading: false,
    occupancy: null,
    away: null,
    lastUpdatedAt: null,
    disabled: false,
    lastError: null,
  };
  if (stored) {
    return {
      ...base,
      paired: true,
      companyId: stored.companyId,
      companyName: stored.companyName,
      officeId: stored.officeId,
      officeLabel: stored.officeLabel,
    };
  }
  return base;
};

export const initialSignageState: SignageState = initialFromStorage();

export const submitSignagePairingCode: any = createAsyncThunk(
  'signage/submitPairingCode',
  async (params: { code: string }, thunkAPI) => {
    try {
      const response = await createSignagePairingRequest(params.code);
      if (!response?.requestId) return thunkAPI.rejectWithValue('Invalid or expired code');
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.message || 'Invalid or expired code');
    }
  }
);

export const pollSignagePairingStatus: any = createAsyncThunk(
  'signage/pollPairingStatus',
  async (params: { requestId: string }, thunkAPI) => {
    try {
      const response = await pollSignagePairingRequest(params.requestId);
      const signage = (thunkAPI.getState() as AppState).signage;
      const stillCurrent = signage.pairingStatus === 'pending' && signage.pendingRequestId === params.requestId;
      if (stillCurrent && response.status === 'APPROVED' && response.deviceToken && response.companyId && response.officeId !== undefined) {
        saveSignageSession({
          deviceToken: response.deviceToken,
          companyId: response.companyId,
          companyName: response.companyName ?? '',
          officeId: response.officeId,
          officeLabel: response.officeLabel ?? '',
        });
      }
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.message || 'Failed to check approval status');
    }
  }
);

export const cancelSignagePairing: any = createAsyncThunk(
  'signage/cancelPairing',
  async (_: void, thunkAPI) => {
    const signage = (thunkAPI.getState() as AppState).signage;
    const requestId = signage.pendingRequestId;
    thunkAPI.dispatch(cancelPairingRequest());
    if (requestId) {
      try { await cancelSignagePairingRequestOnServer(requestId); } catch { /* best effort */ }
    }
  }
);

function toRejection(err: any, thunkAPI: any, fallback: string) {
  if (err instanceof SignageUnauthorizedError) {
    clearSignageSession();
    return thunkAPI.rejectWithValue('UNAUTHORIZED');
  }
  if (err instanceof SignageDisabledError) {
    return thunkAPI.rejectWithValue('DISABLED');
  }
  return thunkAPI.rejectWithValue(err?.message || fallback);
}

export const loadSignageFloors: any = createAsyncThunk(
  'signage/loadFloors',
  async (_: void, thunkAPI) => {
    try {
      return await getSignageFloors();
    } catch (err: any) {
      return toRejection(err, thunkAPI, 'Failed to load floors');
    }
  }
);

export const loadSignageFloorPlan: any = createAsyncThunk(
  'signage/loadFloorPlan',
  async (params: { floorId: number }, thunkAPI) => {
    try {
      return await getSignageFloorPlan(params.floorId);
    } catch (err: any) {
      return toRejection(err, thunkAPI, 'Failed to load floor plan');
    }
  }
);

export const loadSignageOccupancy: any = createAsyncThunk(
  'signage/loadOccupancy',
  async (params: { floorId: number }, thunkAPI) => {
    try {
      return await getSignageOccupancy(params.floorId);
    } catch (err: any) {
      return toRejection(err, thunkAPI, 'Failed to load occupancy');
    }
  }
);

export const loadSignageAway: any = createAsyncThunk(
  'signage/loadAway',
  async (_: void, thunkAPI) => {
    try {
      return await getSignageAway();
    } catch (err: any) {
      return toRejection(err, thunkAPI, 'Failed to load who is away');
    }
  }
);

export const unpairThisSignageScreen: any = createAsyncThunk(
  'signage/unpair',
  async () => {
    try { await unpairSignage(); } catch { /* best effort */ }
    clearSignageSession();
  }
);

function resetToUnpaired(state: SignageState) {
  state.paired = false;
  state.companyId = null;
  state.companyName = '';
  state.officeId = null;
  state.officeLabel = '';
  state.pairingStatus = 'idle';
  state.pendingRequestId = null;
  state.pendingExpiresAt = null;
  state.floors = [];
  state.floorsLoaded = false;
  state.selectedFloorId = null;
  state.floorPlan = null;
  state.occupancy = null;
  state.away = null;
  state.lastUpdatedAt = null;
  state.disabled = false;
}

const signageSlice = createSlice({
  name: 'signage',
  initialState: initialSignageState,
  reducers: {
    clearSignagePairingError(state) { state.pairingError = null; },
    cancelPairingRequest(state) {
      state.pairing = false;
      state.pairingStatus = 'idle';
      state.pendingRequestId = null;
      state.pendingExpiresAt = null;
      state.pollFailureCount = 0;
      state.pairingError = null;
    },
    selectSignageFloor(state, action: PayloadAction<number>) {
      if (state.selectedFloorId === action.payload) return;
      state.selectedFloorId = action.payload;
      state.floorPlan = null;
      state.occupancy = null;
      saveSelectedFloorId(action.payload);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(submitSignagePairingCode.pending, state => {
        state.pairing = true;
        state.pairingStatus = 'idle';
        state.pairingError = null;
      })
      .addCase(submitSignagePairingCode.fulfilled, (state, action: PayloadAction<SignagePairingRequestCreatedResponse>) => {
        state.pairing = false;
        state.pairingStatus = 'pending';
        state.pendingRequestId = action.payload.requestId;
        state.pendingExpiresAt = action.payload.expiresAt;
        state.pollIntervalMs = action.payload.pollIntervalMs || DEFAULT_POLL_INTERVAL_MS;
        state.pollFailureCount = 0;
        state.pairingError = null;
      })
      .addCase(submitSignagePairingCode.rejected, (state, action: any) => {
        state.pairing = false;
        state.pairingStatus = 'error';
        state.pairingError = action.payload || 'Invalid or expired code';
      })
      .addCase(pollSignagePairingStatus.fulfilled, (state, action: any) => {
        const requestId = action.meta?.arg?.requestId;
        if (state.pairingStatus !== 'pending' || state.pendingRequestId !== requestId) return;

        state.pollFailureCount = 0;
        const response: SignagePairingRequestPollResponse = action.payload;
        if (response.status === 'APPROVED' && response.deviceToken) {
          state.pairingStatus = 'approved';
          state.paired = true;
          state.companyId = response.companyId ?? null;
          state.companyName = response.companyName ?? '';
          state.officeId = response.officeId ?? null;
          state.officeLabel = response.officeLabel ?? '';
          state.pendingRequestId = null;
          state.pendingExpiresAt = null;
          state.pairingError = null;
        } else if (response.status === 'APPROVED') {
          state.pairingStatus = 'error';
          state.pendingRequestId = null;
          state.pendingExpiresAt = null;
          state.pairingError = 'Pairing was approved but no credentials were received. Try again.';
        } else if (response.status === 'REJECTED' || response.status === 'CANCELLED') {
          state.pairingStatus = 'rejected';
          state.pendingRequestId = null;
          state.pendingExpiresAt = null;
        } else if (response.status === 'EXPIRED') {
          state.pairingStatus = 'expired';
          state.pendingRequestId = null;
          state.pendingExpiresAt = null;
        }
      })
      .addCase(pollSignagePairingStatus.rejected, (state, action: any) => {
        if (state.pairingStatus !== 'pending') return;
        state.pollFailureCount += 1;
        if (state.pollFailureCount >= MAX_CONSECUTIVE_POLL_FAILURES) {
          state.pairingStatus = 'error';
          state.pendingRequestId = null;
          state.pendingExpiresAt = null;
          state.pairingError = action.payload || 'Failed to check approval status';
        }
      })
      .addCase(loadSignageFloors.fulfilled, (state, action: PayloadAction<SignageFloor[]>) => {
        state.floors = action.payload;
        state.floorsLoaded = true;
        state.disabled = false;
        state.lastError = null;
        const stillValid = state.selectedFloorId !== null
          && action.payload.some(f => f.id === state.selectedFloorId);
        if (!stillValid) {
          state.selectedFloorId = action.payload.length > 0 ? action.payload[0].id : null;
          if (state.selectedFloorId !== null) saveSelectedFloorId(state.selectedFloorId);
        }
      })
      .addCase(loadSignageFloors.rejected, (state, action: any) => {
        handleDataError(state, action.payload, 'Failed to load floors');
        state.floorsLoaded = state.paired && !state.disabled ? state.floorsLoaded : false;
      })
      .addCase(loadSignageFloorPlan.pending, state => { state.floorPlanLoading = true; })
      .addCase(loadSignageFloorPlan.fulfilled, (state, action: PayloadAction<SignageFloorPlan>) => {
        state.floorPlanLoading = false;
        if (state.selectedFloorId !== null && action.payload.floorId !== state.selectedFloorId) return;
        state.floorPlan = action.payload;
        state.disabled = false;
        state.lastError = null;
      })
      .addCase(loadSignageFloorPlan.rejected, (state, action: any) => {
        state.floorPlanLoading = false;
        handleDataError(state, action.payload, 'Failed to load floor plan');
      })
      .addCase(loadSignageOccupancy.fulfilled, (state, action: PayloadAction<SignageOccupancy>) => {
        if (state.selectedFloorId !== null && action.payload.floorId !== state.selectedFloorId) return;
        state.occupancy = action.payload;
        state.lastUpdatedAt = Date.now();
        state.disabled = false;
        state.lastError = null;
      })
      .addCase(loadSignageOccupancy.rejected, (state, action: any) => {
        handleDataError(state, action.payload, 'Failed to load occupancy');
      })
      .addCase(loadSignageAway.fulfilled, (state, action: PayloadAction<SignageAway>) => {
        state.away = action.payload;
        state.disabled = false;
        state.lastError = null;
      })
      .addCase(loadSignageAway.rejected, (state, action: any) => {
        handleDataError(state, action.payload, 'Failed to load who is away');
      })
      .addCase(unpairThisSignageScreen.fulfilled, state => {
        resetToUnpaired(state);
      });
  },
});

function handleDataError(state: SignageState, payload: any, fallback: string) {
  if (payload === 'UNAUTHORIZED') {
    resetToUnpaired(state);
  } else if (payload === 'DISABLED') {
    state.disabled = true;
  } else {
    // Network blip or server error: keep the last good data on screen and let
    // the staleness indicator do the talking.
    state.lastError = payload || fallback;
  }
}

export default signageSlice.reducer;
export const { clearSignagePairingError, selectSignageFloor } = signageSlice.actions;
const { cancelPairingRequest } = signageSlice.actions;

export const selectSignage = (state: AppState) => state.signage;
